import { useState } from 'react'
import { Link2, Plus, VideoOff } from 'lucide-react'

import type { Camera } from '@/components/cameras/camera-types'
import type { CameraTestResult } from '@/lib/api/cameras-api'
import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import { AttachCameraDialog } from '@/components/cameras/attach-camera-dialog'
import { CameraCard } from '@/components/cameras/camera-card'
import { CameraFormDialog } from '@/components/cameras/camera-form-dialog'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    useIrrigationDeviceCameras,
    useSaveCamera,
    useSetIrrigationDeviceCamera,
    useTestCamera,
} from '@/hooks/use-cameras-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    device: IrrigationDevice
    open: boolean
    onOpenChange: (open: boolean) => void
    /** cameras.write: thêm/sửa/gắn/gỡ camera của trạm. */
    canManage: boolean
}

/**
 * Camera của MỘT trạm bơm: thêm camera mới cho trạm, gắn camera có sẵn, sửa,
 * gỡ khỏi trạm. Gỡ chỉ bỏ liên kết — camera vẫn còn (có thể đang ở vùng trồng
 * hoặc trạm khác); xoá hẳn camera làm ở màn Danh sách camera.
 *
 * Lưới chỉ lấy ảnh tĩnh: khung xem trực tiếp nằm trên thẻ trạm.
 */
export function IrrigationDeviceCamerasDialog({
    device,
    open,
    onOpenChange,
    canManage,
}: Props) {
    const { data: cameras = [], isLoading } = useIrrigationDeviceCameras(
        device.id,
    )
    const save = useSaveCamera()
    const station = useSetIrrigationDeviceCamera()
    const test = useTestCamera()

    const [formOpen, setFormOpen] = useState(false)
    const [attachOpen, setAttachOpen] = useState(false)
    const [editing, setEditing] = useState<Camera | null>(null)
    const [testResult, setTestResult] = useState<CameraTestResult | null>(null)

    const openForm = (camera: Camera | null) => {
        setEditing(camera)
        setTestResult(null)
        setFormOpen(true)
    }
    const closeForm = () => {
        setFormOpen(false)
        setEditing(null)
        setTestResult(null)
    }

    const detach = (camera: Camera) => {
        if (
            !window.confirm(
                m.camera_station_detach_confirm({
                    name: camera.name,
                    station: device.name,
                }),
            )
        ) {
            return
        }
        void station
            .mutateAsync({
                cameraId: camera.id,
                deviceId: device.id,
                attach: false,
            })
            .catch(() => undefined)
    }

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[calc(100svh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>
                            {m.camera_station_title({ station: device.name })}
                        </DialogTitle>
                        <DialogDescription>
                            {m.camera_station_desc()}
                        </DialogDescription>
                    </DialogHeader>

                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : cameras.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-dashed bg-muted/40 px-6 py-10 text-center">
                            <VideoOff className="size-6 text-muted-foreground" />
                            <p className="text-sm font-medium">
                                {m.camera_station_empty()}
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2">
                            {cameras.map((camera) => (
                                <CameraCard
                                    key={camera.id}
                                    camera={camera}
                                    active={open && !formOpen}
                                    mode="snapshot"
                                    canWrite={canManage}
                                    onEdit={openForm}
                                    onRemove={detach}
                                    removeMode="unplace"
                                    removeLabel={m.camera_station_detach()}
                                />
                            ))}
                        </div>
                    )}

                    {canManage && (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => openForm(null)}
                            >
                                <Plus className="size-4" />
                                {m.camera_station_add()}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setAttachOpen(true)}
                            >
                                <Link2 className="size-4" />
                                {m.camera_attach_open()}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <CameraFormDialog
                open={formOpen}
                camera={editing}
                isSaving={save.isPending}
                isTesting={test.isPending}
                testResult={testResult}
                onSave={async (values) => {
                    await save.mutateAsync({
                        cameraId: editing?.id,
                        // Thêm mới từ trạm thì gắn luôn vào trạm này.
                        irrigationDeviceId: editing ? undefined : device.id,
                        data: values,
                    })
                    closeForm()
                }}
                onTest={() => {
                    if (!editing) return
                    void test
                        .mutateAsync(editing.id)
                        .then(setTestResult)
                        .catch(() => setTestResult(null))
                }}
                onClose={closeForm}
            />

            <AttachCameraDialog
                open={attachOpen}
                isHere={(c) =>
                    c.irrigation_devices.some((d) => d.device_id === device.id)
                }
                description={m.camera_station_attach_desc()}
                submitLabel={m.camera_station_attach_submit()}
                isSaving={station.isPending}
                onAttach={(cameraId) =>
                    station.mutateAsync({
                        cameraId,
                        deviceId: device.id,
                        attach: true,
                    })
                }
                onClose={() => setAttachOpen(false)}
            />
        </>
    )
}
